// Le visage : sa forme, puis tout ce qui s'y pose (yeux, sourcils, nez, bouche,
// barbe, détails, lunettes, boucles d'oreilles). Les classes d'animation sont
// passées par l'appelant, pour que ces pièces restent de simples dessins.

const INK = "#2B2540";
const GOLD = "#E2B33C";
const MOUTH_DARK = "#3A1D2E";
// Contour des lèvres d'un sourire à dents : une teinte de lèvres, pas l'encre sombre (lue comme
// des lèvres marron), et un trait fin et peu saturé (plus épais ou plus rose, il se lisait comme du maquillage).
const LIP_LINE = "#B4776F";
const TONGUE = "#F08CA0";

export const FACE_PATHS = {
    oval: "M33 49a27 27 0 0 1 54 0v11c0 16-11 27-27 27S33 76 33 60z",
    round: "M32 50a28 28 0 0 1 56 0v8c0 17-12 28-28 28S32 75 32 58z",
    square: "M33 49a27 27 0 0 1 54 0v17c0 13-9 21-19 21H52c-10 0-19-8-19-21z",
    heart: "M33 49a27 27 0 0 1 54 0v8c0 15-15 31-27 31S33 72 33 57z",
};

// `covered` : les cheveux tombent sur l'oreille et n'en laissent voir que le lobe. Le creux
// n'est alors pas dessiné : coupé en deux par la mèche, il se lisait comme une ride au coin de l'œil.
export function Ears({ skin, covered = false }) {
    return (
        <g>
            <circle cx="32.5" cy="58" r="6.5" fill={skin.fill} />
            <circle cx="87.5" cy="58" r="6.5" fill={skin.fill} />
            {!covered && (
                <>
                    <circle cx="32.5" cy="58" r="2.8" fill={skin.shade} />
                    <circle cx="87.5" cy="58" r="2.8" fill={skin.shade} />
                </>
            )}
        </g>
    );
}

export function Earrings({ style }) {
    switch (style) {
        case "studs":
            return (
                <g fill={GOLD}>
                    <circle cx="31" cy="64.5" r="1.8" />
                    <circle cx="89" cy="64.5" r="1.8" />
                </g>
            );
        case "pearls":
            return (
                <g fill="#F7F3EC" stroke="#D9D2C4" strokeWidth="0.5">
                    <circle cx="31" cy="65" r="2.2" />
                    <circle cx="89" cy="65" r="2.2" />
                </g>
            );
        case "drops":
            return (
                <g>
                    <path d="M31 64v3.5M89 64v3.5" stroke={GOLD} strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M31 67c1.6 2 2.2 3.2 2.2 4.2a2.2 2.2 0 0 1-4.4 0c0-1 0.6-2.2 2.2-4.2zM89 67c1.6 2 2.2 3.2 2.2 4.2a2.2 2.2 0 0 1-4.4 0c0-1 0.6-2.2 2.2-4.2z" fill="#4C9FEA" />
                </g>
            );
        case "hoops":
            return (
                <g fill="none" stroke={GOLD} strokeWidth="1.7">
                    <circle cx="31" cy="67" r="3.3" />
                    <circle cx="89" cy="67" r="3.3" />
                </g>
            );
        case "earbuds":
            return (
                <g fill="#fff">
                    <ellipse cx="32.5" cy="58" rx="2.1" ry="2.6" />
                    <ellipse cx="87.5" cy="58" rx="2.1" ry="2.6" />
                    <rect x="31.8" y="59" width="1.5" height="4.6" rx="0.75" />
                    <rect x="86.7" y="59" width="1.5" height="4.6" rx="0.75" />
                </g>
            );
        default:
            return null;
    }
}

function Arc({ cx }) {
    return <path d={`M${cx - 5.8} 58q5.8-7.4 11.6 0`} fill="none" stroke={INK} strokeWidth="2.8" strokeLinecap="round" />;
}

function OpenEye({ cx, style, iris, skin, gazeClass, pupilClass }) {
    if (style === "dot") {
        return (
            <g className={gazeClass}>
                <ellipse cx={cx} cy="56" rx="3.2" ry="3.9" fill={INK} />
                <circle cx={cx + 1.1} cy="54.6" r="1.1" fill="#fff" />
            </g>
        );
    }
    const wide = style === "wide";
    const almond = style === "almond";
    const rx = wide ? 6.6 : almond ? 6.5 : 5.4;
    const ry = wide ? 7.4 : almond ? 4.8 : 6.2;
    const outer = cx < 60 ? -1 : 1;
    return (
        <g>
            <ellipse cx={cx} cy="56" rx={rx} ry={ry} fill="#fff" />
            <g className={gazeClass}>
                <g className={pupilClass}>
                    <circle cx={cx} cy="56.4" r={wide ? 4.3 : 3.7} fill={iris.fill} />
                    <circle cx={cx} cy="56.4" r={wide ? 2.3 : 2} fill={INK} />
                    <circle cx={cx + 1.3} cy="55" r="1.15" fill="#fff" />
                </g>
            </g>
            {style === "sleepy" && (
                <g>
                    <path d={`M${cx - 5.9} 56.5a5.9 6.8 0 0 1 11.8 0z`} fill={skin.fill} />
                    <path d={`M${cx - 5.6} 56.5h11.2`} stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
                </g>
            )}
            {style === "lash" && (
                <path
                    d={`M${cx + outer * 5} 52.6l${outer * 2.6}-2M${cx + outer * 3.6} 50.9l${outer * 1.7}-2.6`}
                    stroke={INK}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                />
            )}
        </g>
    );
}

export function Eyes({ style, iris, skin, className, gazeClass, pupilClass }) {
    return (
        <g className={className}>
            {[48, 72].map((cx) =>
                style === "happy" || (style === "wink" && cx === 72) ? (
                    <Arc key={cx} cx={cx} />
                ) : (
                    <OpenEye key={cx} cx={cx} style={style === "wink" ? "round" : style} iris={iris} skin={skin} gazeClass={gazeClass} pupilClass={pupilClass} />
                ),
            )}
        </g>
    );
}

export function Brows({ style, color, className }) {
    const paths = {
        soft: ["M42.5 46.5q5.5-3 11 0", "M66.5 46.5q5.5-3 11 0"],
        thick: ["M42.5 46.5q5.5-3 11 0", "M66.5 46.5q5.5-3 11 0"],
        arched: ["M42.5 47.5q5-6 11-1.5", "M77.5 47.5q-5-6-11-1.5"],
        straight: ["M42.5 46.5l11-1", "M77.5 46.5l-11-1"],
        raised: ["M42.5 46.5q5.5-3 11 0", "M77.5 45q-5-6-11-1.5"],
        bold: ["M42.5 44.5l11 2.5", "M77.5 44.5l-11 2.5"],
        // Humeur triste (pas un choix du catalogue) : l'intérieur des sourcils remonte.
        sad: ["M42.5 47.5q5-1.5 10.5-4.5", "M77.5 47.5q-5-1.5-10.5-4.5"],
    }[style];
    const width = style === "thick" ? 3.8 : style === "bold" ? 3.2 : 2.4;
    return (
        <g className={className} fill="none" stroke={color.shade} strokeWidth={width} strokeLinecap="round">
            {paths.map((d) => (
                <path key={d} d={d} />
            ))}
        </g>
    );
}

export function Nose({ style, skin }) {
    const stroke = { fill: "none", stroke: skin.shade, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (style) {
        case "round":
            return <path d="M56.4 62.6a3.6 3.2 0 0 0 7.2 0" {...stroke} />;
        case "button":
            return <ellipse cx="60" cy="64" rx="2.5" ry="1.8" fill={skin.shade} />;
        case "pointed":
            return <path d="M60.5 57.5l-3 7h5" {...stroke} />;
        default:
            return <path d="M57.5 63.5q2.5 3 5.5 0.5" {...stroke} />;
    }
}

const EASE = "0.4 0 0.2 1";

// Une animation SMIL ajoutée après coup ne démarre pas seule : on la lance à la main.
// La fonction est définie une fois pour toutes, sinon React la rappellerait à chaque
// rendu et l'animation repartirait du début.
function startAnimation(node) {
    node?.beginElement?.();
}

// Fait passer un attribut d'une forme à l'autre, sans jamais redimensionner la bouche.
// `motion.kind` donne l'allure :
// - "idle" : un aller-retour lent de temps en temps ;
// - "laugh" : la mâchoire qui s'ouvre et se referme en boucle ;
// - "hold" : la forme d'arrivée, prise une fois et gardée (survol, étonnement) ;
// - "release" : le retour à la forme de départ.
// `motion` vaut null quand rien ne doit bouger.
function Morph({ attribute = "d", from, to, motion }) {
    if (!motion) return null;
    if (motion.kind === "hold" || motion.kind === "release") {
        const values = motion.kind === "hold" ? [from, to] : [to, from];
        return (
            <animate
                ref={startAnimation}
                attributeName={attribute}
                dur="0.22s"
                begin="indefinite"
                fill="freeze"
                calcMode="spline"
                values={values.join(";")}
                keyTimes="0;1"
                keySplines={EASE}
            />
        );
    }
    const jaw = motion.kind === "laugh";
    return (
        <animate
            attributeName={attribute}
            dur={jaw ? "0.36s" : "7s"}
            begin={jaw ? "0s" : motion.begin}
            repeatCount="indefinite"
            calcMode="spline"
            values={(jaw ? [from, to, from] : [from, from, to, to, from, from]).join(";")}
            keyTimes={jaw ? "0;0.5;1" : "0;0.5;0.58;0.74;0.82;1"}
            keySplines={Array(jaw ? 2 : 5).fill(EASE).join(";")}
        />
    );
}

const SMILE = ["M51 71q9 8.5 18 0", "M50 70.3q10 10.5 20 0"];
const LAUGH_OPEN = "M47.5 69.5q12.5 2.5 25 0c0 9-5.5 14.5-12.5 14.5s-12.5-5.5-12.5-14.5z";

export function Mouth({ style, motion, lip }) {
    const line = { fill: "none", stroke: INK, strokeWidth: 2.4, strokeLinecap: "round" };
    const laughing = motion?.kind === "laugh";
    switch (style) {
        case "grin":
            return (
                <g>
                    <path d="M50 70q10 3 20 0c0 7-4 11-10 11s-10-4-10-11z" fill={MOUTH_DARK}>
                        <Morph from="M50 70q10 3 20 0c0 7-4 11-10 11s-10-4-10-11z" to="M50 70q10 3 20 0c0 8.6-4 13-10 13s-10-4.4-10-13z" motion={motion} />
                    </path>
                    <path d="M51.5 71q8.5 2 17 0v2q-8.5 2-17 0z" fill="#fff" />
                    <ellipse cx="60" cy="78.5" rx="5" ry="2.6" fill={TONGUE}>
                        <Morph attribute="cy" from="78.5" to="80.2" motion={motion} />
                    </ellipse>
                </g>
            );
        case "laugh": {
            // En plein rire, la mâchoire se referme presque ; au repos, elle ne fait que respirer.
            const closed = laughing
                ? "M47.5 69.5q12.5 2.5 25 0c0 5.5-5.5 9.5-12.5 9.5s-12.5-4-12.5-9.5z"
                : "M47.5 69.5q12.5 2.5 25 0c0 7.5-5.5 12.5-12.5 12.5s-12.5-5-12.5-12.5z";
            return (
                <g>
                    <path d={LAUGH_OPEN} fill={MOUTH_DARK}>
                        <Morph from={LAUGH_OPEN} to={closed} motion={motion} />
                    </path>
                    <path d="M49.5 70.6q10.5 2 21 0v2.2q-10.5 2-21 0z" fill="#fff" />
                    <ellipse cx="60" cy="80.5" rx="6" ry="3" fill={TONGUE}>
                        <Morph attribute="cy" from="80.5" to={laughing ? "76.4" : "78.8"} motion={motion} />
                        <Morph attribute="ry" from="3" to={laughing ? "2.3" : "2.8"} motion={motion} />
                    </ellipse>
                </g>
            );
        }
        case "smirk":
            return (
                <path d="M53 73.5q9 4 15-3.5" {...line}>
                    <Morph from="M53 73.5q9 4 15-3.5" to="M52.5 73.8q9.5 5.2 16.2-4.8" motion={motion} />
                </path>
            );
        case "calm":
            return (
                <path d="M53.5 73.5q6.5 2 13 0" {...line}>
                    <Morph from="M53.5 73.5q6.5 2 13 0" to="M52.5 73q7.5 4.5 15 0" motion={motion} />
                </path>
            );
        case "ooh":
            return (
                <ellipse cx="60" cy="74.5" rx="3.5" ry="4.3" fill={MOUTH_DARK}>
                    <Morph attribute="ry" from="4.3" to="5.4" motion={motion} />
                    <Morph attribute="rx" from="3.5" to="3.9" motion={motion} />
                </ellipse>
            );
        case "teeth":
            // Sourire fermé qui montre les dents.
            return (
                <path d="M50 70q10 4 20 0c-1 5.5-5 8.5-10 8.5s-9-3-10-8.5z" fill="#fff" stroke={LIP_LINE} strokeWidth="1.2" strokeLinejoin="round">
                    <Morph from="M50 70q10 4 20 0c-1 5.5-5 8.5-10 8.5s-9-3-10-8.5z" to="M49 69.4q11 4.6 22 0c-1 6.5-5.5 9.8-11 9.8s-10-3.3-11-9.8z" motion={motion} />
                </path>
            );
        case "serious":
            return (
                <path d="M53.5 74q6.5 0 13 0" {...line}>
                    <Morph from="M53.5 74q6.5 0 13 0" to="M53 74q7 1.6 14 0" motion={motion} />
                </path>
            );
        case "sad":
            return (
                <path d="M52.5 75.5q7.5-4 15 0" {...line}>
                    <Morph from="M52.5 75.5q7.5-4 15 0" to="M52.5 75q7.5-2.6 15 0" motion={motion} />
                </path>
            );
        case "lipsopen":
            return (
                <g>
                    <path d="M50 71c3-2.5 7-2.5 10-1 3-1.5 7-1.5 10 1-2 7.5-6 10.5-10 10.5s-8-3-10-10.5z" fill={lip.fill} />
                    <path d="M52.5 72.5q7.5 2 15 0c-1.5 4.5-4 6.5-7.5 6.5s-6-2-7.5-6.5z" fill={MOUTH_DARK}>
                        <Morph from="M52.5 72.5q7.5 2 15 0c-1.5 4.5-4 6.5-7.5 6.5s-6-2-7.5-6.5z" to="M52.5 72.5q7.5 2 15 0c-1.5 3.6-4 5.2-7.5 5.2s-6-1.6-7.5-5.2z" motion={motion} />
                    </path>
                    <path d="M53.5 72.8q6.5 1.6 13 0v1.8q-6.5 1.6-13 0z" fill="#fff" />
                </g>
            );
        case "kiss":
            return (
                <path d="M55.5 72c1.8-2.4 3.5-2.4 4.5-0.6 1-1.8 2.7-1.8 4.5 0.6 0.6 3.6-1.6 6.8-4.5 6.8s-5.1-3.2-4.5-6.8z" fill={lip.fill} stroke={lip.shade} strokeWidth="0.8" strokeLinejoin="round">
                    <Morph from="M55.5 72c1.8-2.4 3.5-2.4 4.5-0.6 1-1.8 2.7-1.8 4.5 0.6 0.6 3.6-1.6 6.8-4.5 6.8s-5.1-3.2-4.5-6.8z" to="M56.2 72.2c1.5-2.2 3-2.2 3.8-0.6 0.8-1.6 2.3-1.6 3.8 0.6 0.5 3.2-1.4 6-3.8 6s-4.3-2.8-3.8-6z" motion={motion} />
                </path>
            );
        case "lips":
            return (
                <g>
                    <path d="M51.5 72c3-2.2 6-2.2 8.5-0.8 2.5-1.4 5.5-1.4 8.5 0.8-3 5-6 6.5-8.5 6.5s-5.5-1.5-8.5-6.5z" fill={lip.fill}>
                        <Morph
                            from="M51.5 72c3-2.2 6-2.2 8.5-0.8 2.5-1.4 5.5-1.4 8.5 0.8-3 5-6 6.5-8.5 6.5s-5.5-1.5-8.5-6.5z"
                            to="M50.6 71.2c3.4-1.8 6.9-1.6 9.4-0.2 2.5-1.4 6-1.6 9.4 0.2-3.4 6-6.9 7.5-9.4 7.5s-6-1.5-9.4-7.5z"
                            motion={motion}
                        />
                    </path>
                    <path d="M51.5 72q8.5 2.6 17 0" fill="none" stroke={lip.shade} strokeWidth="1.2" strokeLinecap="round">
                        <Morph from="M51.5 72q8.5 2.6 17 0" to="M50.6 71.2q9.4 3.6 18.8 0" motion={motion} />
                    </path>
                </g>
            );
        default:
            return (
                <path d={SMILE[0]} {...line}>
                    <Morph from={SMILE[0]} to={SMILE[1]} motion={motion} />
                </path>
            );
    }
}

// Les barbes débordent exprès du visage : c'est la découpe (`clip`) qui leur donne le
// contour de la mâchoire, donc elles épousent chaque forme de visage, ovale, ronde,
// carrée ou en cœur. Seule la ligne du haut (joues, tour de bouche) est dessinée ici.
const BEARD = "M20 56h13v2c3 8 8 11.5 15.5 10 4-3.5 19-3.5 23 0 7.5 1.5 12.5-2 15.5-10v-2h13v46H20z";
const SHORT_BEARD = "M20 62h13.5v2c4 6 9 8.2 14.5 7.2 3-3.5 21-3.5 24 0 5.5 1 10.5-1.2 14.5-7.2v-2H100v40H20z";
// Même mâchoire, avec la lèvre supérieure en plus et le tour de bouche dégagé.
// La ligne des joues part de la patte, plonge vers le coin de la bouche et remonte sous
// le nez. Les deux zones débordent du visage : la découpe leur donne la mâchoire.
const STUBBLE = "M20 60h13c2.5 7 7.5 11 15 10.5 2.5-3.5 6.5-5 12-5s9.5 1.5 12 5c7.5 0.5 12.5-3.5 15-10.5h13v46H20z";
// La même, trois unités plus haut : une frange de poils clairsemés au-dessus de la zone dense.
const STUBBLE_FRINGE = "M20 56h13c2 7.5 7 12 15.5 11.5 2.5-4 6.5-6 11.5-6s9 2 11.5 6c8.5 0.5 13.5-4 15.5-11.5h13v50H20z";

// `clip` garde la barbe dans la forme du visage choisi ; `jawClip` n'en garde que le bas.
export function FacialHair({ style, color, clip, jawClip, facePath, patternId }) {
    switch (style) {
        case "moustache":
            return <path d="M50 68.5c3-3.5 7.5-3 10-1 2.5-2 7-2.5 10 1-3 2.5-7 2.5-10 1-3 1.5-7 1.5-10-1z" fill={color.fill} />;
        case "handlebar":
            return <path d="M46 66.5c4.5-0.5 9.5-0.5 14 2 4.5-2.5 9.5-2.5 14-2-1 5-7 6-14 3.5-7 2.5-13 1.5-14-3.5z" fill={color.fill} />;
        case "pencil":
            return <path d="M51.5 68.6q8.5-3.2 17 0" fill="none" stroke={color.fill} strokeWidth="2" strokeLinecap="round" />;
        case "walrus":
            return <path d="M48 66.5c4-3.2 9-3.2 12-1 3-2.2 8-2.2 12 1 1 5.2-1 9-4.2 10-1.8-3.2-4.8-4.3-7.8-4.3s-6 1.1-7.8 4.3c-3.2-1-5.2-4.8-4.2-10z" fill={color.fill} />;
        case "soulpatch":
            return <path d="M57.5 81.5h5l-1.2 5.5h-2.6z" fill={color.fill} />;
        case "vandyke":
            return (
                <g fill={color.fill}>
                    <path d="M46 66.5c4.5-0.5 9.5-0.5 14 2 4.5-2.5 9.5-2.5 14-2-1 5-7 6-14 3.5-7 2.5-13 1.5-14-3.5z" />
                    <path d="M54.5 80c2-2 9-2 11 0l-5.5 12z" />
                </g>
            );
        case "circle":
            // Moustache et bouc reliés autour de la bouche.
            return (
                <path
                    fillRule="evenodd"
                    d="M49.5 67.5c0-2.5 4.5-4 10.5-4s10.5 1.5 10.5 4c1 10.5-3.5 20-10.5 20s-11.5-9.5-10.5-20zM52.8 71.5c0 5.5 3 9 7.2 9s7.2-3.5 7.2-9c-2.5-1.6-11.9-1.6-14.4 0z"
                    fill={color.fill}
                    clipPath={clip}
                />
            );
        case "chinstrap":
            // Le contour du visage lui-même, épaissi vers l'intérieur et gardé sous les oreilles.
            return (
                <g clipPath={jawClip}>
                    <path d={facePath} fill="none" stroke={color.fill} strokeWidth="9" clipPath={clip} />
                </g>
            );
        case "sideburns":
            return <path d="M20 52h17.5c0.2 4.3 1 7.8 3 11l-1 9.5c-3.5-3-6-7.5-7-13H20zM100 52H82.5c-0.2 4.3-1 7.8-3 11l1 9.5c3.5-3 6-7.5 7-13H100z" fill={color.fill} clipPath={clip} />;
        case "shortbeard":
            // Barbe courte taillée : la ligne des joues est nette et plus basse.
            return <path d={SHORT_BEARD} fill={color.fill} clipPath={clip} />;
        case "goatee":
            return (
                <g fill={color.fill}>
                    <path d="M50 68.5c3-3.5 7.5-3 10-1 2.5-2 7-2.5 10 1-3 2.5-7 2.5-10 1-3 1.5-7 1.5-10-1z" />
                    <path d="M53.5 80c2-2.5 11-2.5 13 0 1 5.5-2 9-6.5 9s-7.5-3.5-6.5-9z" />
                </g>
            );
        case "beard":
            return <path d={BEARD} fill={color.fill} clipPath={clip} />;
        case "stubble":
            // Un semis de points serrés sur un voile très léger : on lit du poil, pas un aplat.
            // Deux zones superposées, sans masque ni flou (leur rendu variait d'un navigateur
            // à l'autre) : une frange claire le long des joues, puis la zone dense. La barbe
            // naissante s'estompe vers le haut et couvre tout le bas du visage, bouche comprise.
            return (
                <g clipPath={clip}>
                    <pattern id={patternId} width="2.4" height="2.4" patternUnits="userSpaceOnUse" patternTransform="rotate(28)">
                        <circle cx="0.6" cy="0.6" r="0.4" fill={color.fill} />
                        <circle cx="1.8" cy="1.8" r="0.34" fill={color.fill} />
                    </pattern>
                    <path d={STUBBLE_FRINGE} fill={`url(#${patternId})`} opacity="0.32" />
                    <path d={STUBBLE} fill={color.fill} opacity="0.08" />
                    <path d={STUBBLE} fill={`url(#${patternId})`} opacity="0.55" />
                </g>
            );
        case "longbeard":
            // La barbe du visage, prolongée d'une pointe qui descend sous le menton.
            return (
                <g fill={color.fill}>
                    <path d="M39 76c1 13 9.5 22 21 22s20-9 21-22z" />
                    <path d={BEARD} clipPath={clip} />
                </g>
            );
        default:
            return null;
    }
}

// Les marques de l'âge, dans la teinte d'ombre de la peau. Du plus discret au plus net :
// une seule zone à peine marquée (plis du sourire, coin des yeux, front), puis les trois
// ensemble, puis les mêmes en plus appuyé avec les poches sous les yeux.
export function Wrinkles({ style, skin }) {
    if (style === "none") return null;
    const marked = style === "marked";
    const all = marked || style === "light";
    // Une zone seule reste très fine et à demi transparente.
    const faint = !all;
    return (
        <g fill="none" stroke={skin.shade} strokeWidth={faint ? 1 : 1.3} strokeLinecap="round" opacity={faint ? 0.75 : 1}>
            {(all || style === "smile") && <path d="M50.5 65.5q-2.6 3-2 6.8M69.5 65.5q2.6 3 2 6.8" />}
            {(all || style === "eyes") && <path d="M39.5 54.8l-2.6-1.2M39.3 57.6l-2.7 0.7M80.5 54.8l2.6-1.2M80.7 57.6l2.7 0.7" />}
            {(marked || style === "forehead") && <path d="M48.5 37.8q11.5-2.6 23 0M51 41.8q9-2 18 0" />}
            {marked && <path d="M43.5 63.3q4.5 2 9 0M67.5 63.3q4.5 2 9 0" />}
        </g>
    );
}

export function Details({ style, skin }) {
    switch (style) {
        case "freckles":
            return (
                <g fill={skin.shade}>
                    {[[41.5, 63.5], [44.5, 66], [39.5, 66.5], [78.5, 63.5], [75.5, 66], [80.5, 66.5]].map(([cx, cy]) => (
                        <circle key={cx} cx={cx} cy={cy} r="1" />
                    ))}
                </g>
            );
        case "blush":
            return (
                <g fill="#F2788A" opacity="0.34">
                    <ellipse cx="42" cy="65.5" rx="4.6" ry="2.9" />
                    <ellipse cx="78" cy="65.5" rx="4.6" ry="2.9" />
                </g>
            );
        case "mole":
            return <circle cx="71.5" cy="69" r="1.25" fill="#4A2E22" />;
        case "nosestud":
            return <circle cx="63.2" cy="64" r="1.1" fill="#E8E8EE" stroke="#A9ABB8" strokeWidth="0.4" />;
        case "septum":
            return <path d="M58 66.2a2.1 2.1 0 0 0 4 0" fill="none" stroke="#C9CED6" strokeWidth="1.3" strokeLinecap="round" />;
        case "scar":
            return <path d="M74.5 44l3.5 8M73.8 47.2l3.4-1.2M75.2 50.2l3.4-1.2" fill="none" stroke={skin.shade} strokeWidth="1.3" strokeLinecap="round" />;
        case "plaster":
            return (
                <g transform="rotate(-22 77 66)">
                    <rect x="71.5" y="63.5" width="11" height="5" rx="2.5" fill="#F6DDBF" stroke="#D8B68E" strokeWidth="0.6" />
                    <rect x="75.2" y="63.5" width="3.6" height="5" fill="#EBC89F" />
                </g>
            );
        default:
            return null;
    }
}

export function Glasses({ style }) {
    if (style === "none") return null;
    const bridge = <path d="M57.5 55q2.5-2 5 0" fill="none" stroke={INK} strokeWidth="2" strokeLinecap="round" />;
    switch (style) {
        case "round":
            return (
                <g>
                    <g fill="#fff" fillOpacity="0.18" stroke={INK} strokeWidth="2">
                        <circle cx="48" cy="56" r="9" />
                        <circle cx="72" cy="56" r="9" />
                    </g>
                    {bridge}
                </g>
            );
        case "thin":
            return (
                <g>
                    <g fill="#fff" fillOpacity="0.12" stroke={GOLD} strokeWidth="1.4">
                        <rect x="38.5" y="49.5" width="18.5" height="13" rx="4" />
                        <rect x="63" y="49.5" width="18.5" height="13" rx="4" />
                    </g>
                    <path d="M57 55q3-1.6 6 0" fill="none" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" />
                </g>
            );
        case "cat":
            return (
                <g>
                    <g fill="#fff" fillOpacity="0.18" stroke={INK} strokeWidth="2" strokeLinejoin="round">
                        <path d="M36.5 48.5l21 2.5v5.5c0 4.5-3.5 7.5-8.5 7.5h-2c-5 0-9-4-9.5-9z" />
                        <path d="M83.5 48.5l-21 2.5v5.5c0 4.5 3.5 7.5 8.5 7.5h2c5 0 9-4 9.5-9z" />
                    </g>
                    {bridge}
                </g>
            );
        case "roundsun":
            return (
                <g>
                    <g fill={INK} fillOpacity="0.92" stroke={INK} strokeWidth="2">
                        <circle cx="48" cy="56" r="8.6" />
                        <circle cx="72" cy="56" r="8.6" />
                    </g>
                    {bridge}
                    <path d="M43 52.5l4-2.5M67 52.5l4-2.5" stroke="#fff" strokeOpacity="0.35" strokeWidth="1.6" strokeLinecap="round" />
                </g>
            );
        case "half":
            return (
                <g fill="none" stroke={INK} strokeLinecap="round">
                    <path d="M38.5 51.5q9.5-2.5 19 0M62.5 51.5q9.5-2.5 19 0" strokeWidth="2.8" />
                    <path d="M39 52.5v3.5a9 8 0 0 0 18 0v-3.5M63 52.5v3.5a9 8 0 0 0 18 0v-3.5" strokeWidth="1" opacity="0.55" />
                    <path d="M57.5 53q2.5-1.6 5 0" strokeWidth="2" />
                </g>
            );
        case "heart": {
            const lens = "c-6.5-4.2-9.5-7.6-9.5-11.2 0-3 2.3-5.1 5-5.1 1.9 0 3.5 1 4.5 2.6 1-1.6 2.6-2.6 4.5-2.6 2.7 0 5 2.1 5 5.1 0 3.6-3 7-9.5 11.2z";
            return (
                <g>
                    <g fill="#F2558F" fillOpacity="0.82" stroke="#C93A72" strokeWidth="1.5" strokeLinejoin="round">
                        <path d={`M48 64.5${lens}`} />
                        <path d={`M72 64.5${lens}`} />
                    </g>
                    <path d="M57 54.5q3-1.6 6 0" fill="none" stroke="#C93A72" strokeWidth="1.5" strokeLinecap="round" />
                </g>
            );
        }
        case "aviator":
            return (
                <g>
                    <g fill="#5B4A2A" fillOpacity="0.55" stroke={GOLD} strokeWidth="1.6">
                        <path d="M39 50h18v6.5c0 5-3.5 9-9 9s-9-4-9-9z" />
                        <path d="M63 50h18v6.5c0 5-3.5 9-9 9s-9-4-9-9z" />
                    </g>
                    <path d="M57 51.5h6M57 55q3-1.6 6 0" fill="none" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" />
                </g>
            );
        default: {
            const sun = style === "sun";
            return (
                <g>
                    <g fill={sun ? INK : "#fff"} fillOpacity={sun ? 0.92 : 0.18} stroke={INK} strokeWidth="2">
                        <rect x="38.5" y="48" width="19" height="15.5" rx={sun ? 6 : 4} />
                        <rect x="62.5" y="48" width="19" height="15.5" rx={sun ? 6 : 4} />
                    </g>
                    {bridge}
                    {sun && <path d="M43 52l5-2.5M67 52l5-2.5" stroke="#fff" strokeOpacity="0.35" strokeWidth="1.6" strokeLinecap="round" />}
                </g>
            );
        }
    }
}
